import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDocumentWindow } from '../data/useDocumentWindow';
import { COLLECTIONS } from '../data/paths';
import { frozenBodyOf } from '../domain/documentRender';
import { logoFingerprint, normaliseLogo } from '../domain/logo';

// The logo an invoice is drawn with on screen (#622).
//
// A frozen invoice (src/domain/documentRender.js) keeps the fingerprint of the
// logo it was drawn with, and the server keeps that logo once at
// `invoiceLogos/{fingerprint}`. So it shows the logo it was sent with: the
// studio's current one when that is the same logo, which costs no read, and
// otherwise the kept copy, read once. One that cannot be read (offline, never
// synced) shows no logo rather than the current one, which would be a page
// that was never sent. A frozen invoice with no logo has none; an invoice
// issued before #622 is not frozen and is drawn with the studio's logo as it
// is now.
export default function useInvoiceLogo(invoice, currentLogo = null) {
  const { user } = useAuth();
  const frozen = Boolean(frozenBodyOf(invoice));
  const fingerprint = frozen ? invoice.frozen.logo || null : null;
  const current = useMemo(() => logoFingerprint(currentLogo), [currentLogo]);
  const same = Boolean(fingerprint) && current === fingerprint;
  const read = useDocumentWindow({
    uid: fingerprint && !same ? user?.uid || null : null,
    collection: COLLECTIONS.invoiceLogos,
    id: fingerprint || 'none',
  });
  if (!frozen) return currentLogo || null;
  if (!fingerprint) return null;
  if (same) return currentLogo;
  return normaliseLogo(read.data?.logo) || null;
}
